import {
  Alert,
  Badge,
  Button,
  Center,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useBrandsStore } from "./brandsStore";
import CreateBrand from "./CreateBrand";
import { useEffect, useState } from "react";
import DetectBrands from "./DetectBrands";
import { useLocation } from "wouter";
import BrandLogo from "../_Shared/BrandLogo";
import {
  IconAsterisk,
  IconEye,
  IconFlask,
  IconPlus,
  IconRefresh,
  IconTools,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import BeaconLoader from "../_Shared/BeaconLoader";
import { MetricDelta } from "../_Shared/metricDelta";
import { useStore } from "../store";
import { useBrandContext } from "./brandContext";
import { jsonGet, jsonPost } from "../api";

const DEMO_BRAND_NAME = "TrailPeak";

function BrandStatusBadge({ status, isDemo }) {
  const statusColors = {
    PreReview: "red",
    MonitorSetup: "yellow",
    Active: "green",
    Inactive: "gray",
  };

  const icons = {
    PreReview: <IconTools size={16} stroke={1.5} />,
    MonitorSetup: <IconTools size={16} stroke={1.5} />,
    Active: <BeaconLoader size={12} />,
    Inactive: <IconX size={16} stroke={1.5} />,
  };

  return (
    <>
      {isDemo && (
        <Tooltip label="Sample brand with fabricated data — explore freely, it never counts against your plan">
          <Badge color="grape" variant="light" size="sm">
            Sample
          </Badge>
        </Tooltip>
      )}
      <Badge
        leftSection={icons[status]}
        color={statusColors[status] || "gray"}
        variant="light"
      >
        {status}
      </Badge>
    </>
  );
}

function BrandRelationshipBadge({ relationshipType, canEdit }) {
  const relationshipColors = {
    PrimaryOwner: "blue",
    TrackedCompetitor: "orange",
    MarketResearch: "purple",
    SharedAccess: "green",
  };

  const relationshipLabels = {
    PrimaryOwner: "Owner",
    TrackedCompetitor: "Competitor",
    MarketResearch: "Research",
    SharedAccess: "Shared",
  };

  return (
    <Badge
      color={relationshipColors[relationshipType] || "gray"}
      variant="light"
      size="sm"
    >
      {relationshipLabels[relationshipType] || relationshipType}
      {!canEdit && " (View Only)"}
    </Badge>
  );
}

function MetricBadge({ icon, label, value, trend, metric }) {
  if (value == null) return null;
  return (
    <Group gap={4} wrap="nowrap">
      {icon}
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="xs" fw={600}>
        {value.toLocaleString()}
      </Text>
      <MetricDelta metric={metric} value={trend} hideWhenFlat />
    </Group>
  );
}

export default function BrandList() {
  const { brands, fetchBrands, isLoading } = useBrandsStore();
  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [detectModalOpened, setDetectModalOpened] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [installingDemo, setInstallingDemo] = useState(false);
  const tenantProfile = useStore((state) => state.tenantProfile);
  const { setSelectedBrand } = useBrandContext();
  const [, setLocation] = useLocation();

  useEffect(() => {
    fetchBrands();
    jsonGet("brands/overview-metrics")
      .then((data) => {
        if (data) {
          const indexed = {};
          for (const m of data) indexed[m.brandId] = m;
          setMetrics(indexed);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <Container>
      {/* <h1>Your Monitored Brands</h1> */}
      {isLoading && (
        <Center mt="xl">
          <BeaconLoader size={48} />
        </Center>
      )}
      {!isLoading && brands && brands.length === 0 && (
        <Center>
          <Alert ta="center" icon={<IconAsterisk size={32} />} color="gray">
            You have no brands yet. Click "Create New Brand" to get started!
          </Alert>
        </Center>
      )}

      {!isLoading && brands && brands.length > 0 && (
        <Stack>
          {brands.map((brand) => (
            <Paper
              p="md"
              withBorder
              style={{ cursor: "pointer" }}
              key={brand.id}
              onClick={() => {
                setSelectedBrand(brand);
                setLocation("/dashboard");
              }}
            >
              <Group wrap="nowrap">
                <BrandLogo brand={brand} size={64} />

                <Stack w="100%" gap={4}>
                  <Group justify="space-between" align="center">
                    <Title order={4}>{brand.name}</Title>
                    <Group gap="xs">
                      <BrandRelationshipBadge
                        relationshipType={brand.relationshipType}
                        canEdit={brand.canEdit}
                      />
                      <BrandStatusBadge
                        status={brand.setupStatus}
                        isDemo={brand.isDemo}
                      />
                    </Group>
                  </Group>
                  <Text fz="sm" lineClamp={2}>
                    {brand.descriptionNative ?? brand.description}
                  </Text>
                  {metrics[brand.id] && (
                    <Group gap="lg" mt={4}>
                      <MetricBadge
                        icon={<IconEye size={14} />}
                        label="Pageviews (7d)"
                        value={metrics[brand.id].pageviews}
                        trend={metrics[brand.id].pageviewsTrend}
                        metric="pageviews"
                      />
                      <MetricBadge
                        icon={<IconUsers size={14} />}
                        label="Visitors (7d)"
                        value={metrics[brand.id].uniqueVisitors}
                        trend={metrics[brand.id].visitorsTrend}
                        metric="visitors"
                      />
                    </Group>
                  )}
                </Stack>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      <Group wrap="nowrap" justify="flex-end" mt="md">
        <Tooltip
          label="Installs a sample brand with four weeks of fabricated data — no setup, nothing real"
        >
          <Button
            variant="subtle"
            leftSection={<IconFlask size={16} />}
            loading={installingDemo}
            onClick={async () => {
              setInstallingDemo(true);
              try {
                await jsonPost("demo-brand", {});
                await fetchBrands();
                const refreshed = useBrandsStore.getState().brands;
                const demo = refreshed?.find((b) => b.name === DEMO_BRAND_NAME);
                if (demo) {
                  setSelectedBrand(demo);
                  setLocation("/dashboard");
                }
              } finally {
                setInstallingDemo(false);
              }
            }}
          >
            Try with sample data
          </Button>
        </Tooltip>

        <Button
          variant="outline"
          leftSection={<IconRefresh size={16} />}
          onClick={fetchBrands}
        >
          Refresh
        </Button>

        <Tooltip
          label={
            tenantProfile?.usage?.brandsUsed >=
            tenantProfile?.usage?.brandsLimit
              ? "You have reached your plan's limit on brands tracked"
              : "Create a new brand to monitor"
          }
        >
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setItemToEdit(null);
              setCreateModalOpened(true);
            }}
            disabled={
              tenantProfile?.usage?.brandsUsed >=
              tenantProfile?.usage?.brandsLimit
            }
          >
            Create New Brand
          </Button>
        </Tooltip>
      </Group>

      <CreateBrand
        modalOpened={createModalOpened}
        setModalOpened={setCreateModalOpened}
        initialValues={itemToEdit}
      />

      <DetectBrands
        modalOpened={detectModalOpened}
        setModalOpened={setDetectModalOpened}
      />
    </Container>
  );
}
